import { Grid, Paper } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import PageHeading from '../../../../../../Components/PageHeading';
import SwitchInput from '../../../../../../Components/SwitchInput';
import DropDownSingle from '../../../../../../Components/DropDownSingle';
import InputConfig from '../../../../../../Components/InputConfig';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { UnitsContext } from '../../../../../../Contexts/UnitsContext';
import useCommonStyles from '../../../../../../commonStyles';
import TP_VAST_CONSTANT from '../../constant.json';
import { toast } from 'react-toastify';

/**
 * @description A configuration panel for managing third-party Vast reporting settings.
 */
const TpVastReportingConfig = () => {
	const { tpVastRawConfig, updateTpVastRawConfig } = useContext(UnitsContext);
	const commonStyleClasses = useCommonStyles();

	/**
	 * @description Gets demand config keys that are not present in the existing demandConfiguration.
	 * @returns {string[]} List of unused demand config keys.
	 */
	const filteredDemandConfigKeys = useMemo(() => {
		const existingKeys = Object.keys(tpVastRawConfig?.demandConfiguration || {});
		const unusedKeys = [];

		TP_VAST_CONSTANT.TP_VAST_DEMAND_CONFIG.forEach(({ value }) => {
			if (!existingKeys.includes(value)) {
				unusedKeys.push(value);
			}
		});

		return unusedKeys;
	}, [tpVastRawConfig]);

	/**
	 * @name updateDemandConfig This function updates the demand configuration
	 */
	const updateDemandConfig = (oldKey, newkey) => {
		if (!oldKey || !newkey || oldKey === newkey) return;

		const cloneTpVastConfig = structuredClone(tpVastRawConfig);

		if (cloneTpVastConfig.demandConfiguration[newkey]) {
			toast.error(`Demand configuration with key "${newkey}" already exists.`, {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}

		cloneTpVastConfig.demandConfiguration[newkey] = cloneTpVastConfig.demandConfiguration[oldKey];
		delete cloneTpVastConfig.demandConfiguration[oldKey];

		updateTpVastRawConfig(cloneTpVastConfig);
	};

	/**
	 * @name AddDemandConfig Adds a new demand configuration if available
	 */
	const AddDemandConfig = () => {
		if (filteredDemandConfigKeys.length === 0) {
			toast.error('No available demand config keys to add', {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}

		const newKey = filteredDemandConfigKeys[0];
		const cloneTpVastConfig = structuredClone(tpVastRawConfig);

		if (cloneTpVastConfig.demandConfiguration[newKey]) {
			toast.error(`Demand configuration "${newKey}" already exists.`, {
				position: toast.POSITION.BOTTOM_RIGHT
			});
			return;
		}

		cloneTpVastConfig.demandConfiguration[newKey] = {};

		updateTpVastRawConfig(cloneTpVastConfig);
	};

	/**
	 * @name updateDemandConfigurationProperty Updates a property in the demand configuration
	 */
	const updateDemandConfigurationProperty = (demandKey, property, value) => {
		if (!demandKey || !property || value == null || value == undefined) {
			toast.error('Invalid input: Demand key, property name, and value are required.');
			return;
		}

		const cloneTpVastConfig = structuredClone(tpVastRawConfig || {});

		if (!cloneTpVastConfig.demandConfiguration) {
			cloneTpVastConfig.demandConfiguration = {};
		}

		if (!cloneTpVastConfig.demandConfiguration[demandKey]) {
			cloneTpVastConfig.demandConfiguration[demandKey] = {};
		}

		cloneTpVastConfig.demandConfiguration[demandKey][property] = value;
		updateTpVastRawConfig(cloneTpVastConfig);
	};

	return (
		<Paper className={commonStyleClasses.mainPagePaperMarginStyle} style={{ marginTop: '3rem' }}>
			<PageHeading
				headingName="Third Party Vast Reporting"
				headingPath={['Dashboard', 'Framerate', 'Third Party Vast Reporting']}
				description="Manage Third Party Vast Reponting Config"
			/>

			<Grid
				container
				rowSpacing={1}
				columnSpacing={1}
				gap={3}
				style={{ margin: '3rem 0 2rem 5rem', width: '96%' }}
			>
				<Grid item xs={12}>
					<SwitchInput
						keyData={'tpVastReportingEnabled'}
						label={'Enabled'}
						disabled={false}
						defaultChecked={false}
						containerStyles={{ marginTop: '3rem', width: '10%' }}
						object={tpVastRawConfig}
						updateObject={updateTpVastRawConfig}
					/>
				</Grid>

				{/* Display all configured demand partners */}
				{Object.keys(tpVastRawConfig?.demandConfiguration || {}).map((key) => (
					<Grid container item xs={12} key={key} spacing={2}>
						{/* Select or update the demand configuration */}
						<Grid item xs={4}>
							<DropDownSingle
								label={'Select Demand Config'}
								isObjectEditor={false}
								list={TP_VAST_CONSTANT.TP_VAST_DEMAND_CONFIG}
								formControlVariant="outlined"
								selectInputStyles={{ marginTop: '.5rem' }}
								directValue={key}
								updateDirectValue={(newkey) => updateDemandConfig(key, newkey)}
							/>
						</Grid>

						{/* Set revenue share for the demand partner */}
						<Grid item xs={3}>
							<InputConfig
								label="Demand Partner Cut"
								inputType="number"
								isObjectEditor={false}
								containerStyles={{ marginTop: '1rem' }}
								directValue={tpVastRawConfig.demandConfiguration[key]?.demandPartnerCut}
								updateDirectValue={(value) =>
									updateDemandConfigurationProperty(key, 'demandPartnerCut', value)
								}
							/>
						</Grid>

						{/* Enable or disable reporting for the demand partner */}
						<Grid item xs={3}>
							<SwitchInput
								label={'Reporting Enabled'}
								disabled={false}
								isObjectEditor={false}
								defaultChecked={false}
								containerStyles={{ marginTop: '5rem', marginLeft: '4rem', width: '90%' }}
								directValue={tpVastRawConfig.demandConfiguration?.[key]?.reportingEnabled}
								updateDirectValue={(value) =>
									updateDemandConfigurationProperty(key, 'reportingEnabled', value)
								}
							/>
						</Grid>
					</Grid>
				))}
				{/*  Show Add Demand Config Icon only if there are available demand config keys to add */}
				{filteredDemandConfigKeys.length > 0 && (
					<Grid item xs={10} style={{ display: 'flex', justifyContent: 'center' }}>
						<AddCircleIcon color="primary" fontSize="large" onClick={() => AddDemandConfig()} />
					</Grid>
				)}
			</Grid>
		</Paper>
	);
};

export default TpVastReportingConfig;
